import type { UserSourceHeaderName } from './config';
import { sanitizeTrackingValue } from './trackingValue';

export const CUSTOMER_SOURCE_HEADERS_KEY = 'customer_source_headers';
const ATTRIBUTION_TTL_DAYS = 30;
const ATTRIBUTION_TTL_MS = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Browser-local attribution cache.
 *
 * Parsed URL source params are stored as backend-ready `x-*` headers so later
 * registration/API requests can keep attribution even after navigation.
 */
type StoredCustomerSourceHeaders = {
  headers: Record<string, string>;
  expiresAt: number;
  /** Single-use headers that were already consumed; they must not be re-stored from the URL. */
  expiredHeaders?: string[];
};

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeExpiredHeaders(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export function readExpiredCustomerSourceHeadersFromStorage(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(CUSTOMER_SOURCE_HEADERS_KEY);
    const parsed = raw ? JSON.parse(raw) as unknown : null;

    if (!isObject(parsed) || (typeof parsed.expiresAt === 'number' && Date.now() > parsed.expiresAt)) {
      return [];
    }

    return sanitizeExpiredHeaders(parsed.expiredHeaders);
  } catch {
    return [];
  }
}

function sanitizeHeaders(headers: Record<string, unknown>): Record<string, string> {
  return Object.entries(headers).reduce<Record<string, string>>((acc, [name, value]) => {
    const normalizedValue = sanitizeTrackingValue(value);
    if (normalizedValue) {
      acc[name] = normalizedValue;
    }
    return acc;
  }, {});
}

export function readCustomerSourceHeadersFromStorage(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(CUSTOMER_SOURCE_HEADERS_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;

    // Backward compatibility with previous format: plain header object.
    if (isObject(parsed) && !('headers' in parsed) && !('expiresAt' in parsed)) {
      return sanitizeHeaders(parsed);
    }

    if (!isObject(parsed) || !isObject(parsed.headers) || typeof parsed.expiresAt !== 'number') {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return {};
    }

    if (Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return {};
    }

    return sanitizeHeaders(parsed.headers);
  } catch {
    return {};
  }
}

/**
 * Merges newly parsed attribution headers into the existing 30-day cache.
 * New values win, missing values do not clear older attribution.
 */
export function writeCustomerSourceHeadersToStorage(
  headers: Record<string, string>,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const expiredHeaders = readExpiredCustomerSourceHeadersFromStorage();
  const sanitizedIncoming = sanitizeHeaders(headers);
  expiredHeaders.forEach(name => delete sanitizedIncoming[name]);
  if (!Object.keys(sanitizedIncoming).length) {
    return;
  }

  const existing = readCustomerSourceHeadersFromStorage();
  const merged = {
    ...existing,
    ...sanitizedIncoming,
  };

  const payload: StoredCustomerSourceHeaders = {
    headers: merged,
    expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
    ...(expiredHeaders.length ? { expiredHeaders } : {}),
  };

  window.localStorage.setItem(CUSTOMER_SOURCE_HEADERS_KEY, JSON.stringify(payload));
}

/**
 * Marks a single-use header as consumed: removes its value and remembers its name,
 * so the same URL param is not written back to storage until the cache expires.
 */
export function markCustomerSourceHeaderExpired(headerName: UserSourceHeaderName): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const raw = window.localStorage.getItem(CUSTOMER_SOURCE_HEADERS_KEY);
    const parsed = raw ? JSON.parse(raw) as unknown : null;
    const headers = readCustomerSourceHeadersFromStorage();
    delete headers[headerName];

    const expiredHeaders = Array.from(new Set([...readExpiredCustomerSourceHeadersFromStorage(), headerName]));
    const expiresAt = isObject(parsed) && typeof parsed.expiresAt === 'number' && Date.now() <= parsed.expiresAt
      ? parsed.expiresAt
      : Date.now() + ATTRIBUTION_TTL_MS;

    const payload: StoredCustomerSourceHeaders = { headers, expiresAt, expiredHeaders };
    window.localStorage.setItem(CUSTOMER_SOURCE_HEADERS_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors; attribution cleanup should not block auth flows.
  }
}

/** Forgets consumed headers, so a new value for them can be stored again. */
export function removeExpiredCustomerSourceHeaders(headerNames: string[]): void {
  if (typeof window === 'undefined' || !headerNames.length) {
    return;
  }

  try {
    const raw = window.localStorage.getItem(CUSTOMER_SOURCE_HEADERS_KEY);
    const parsed = raw ? JSON.parse(raw) as unknown : null;

    if (!isObject(parsed) || !isObject(parsed.headers) || typeof parsed.expiresAt !== 'number') {
      return;
    }

    const expiredHeaders = sanitizeExpiredHeaders(parsed.expiredHeaders).filter(name => !headerNames.includes(name));
    const headers = sanitizeHeaders(parsed.headers);

    if (!Object.keys(headers).length && !expiredHeaders.length) {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return;
    }

    const payload: StoredCustomerSourceHeaders = {
      headers,
      expiresAt: parsed.expiresAt,
      ...(expiredHeaders.length ? { expiredHeaders } : {}),
    };

    window.localStorage.setItem(CUSTOMER_SOURCE_HEADERS_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors; attribution cleanup should not block auth flows.
  }
}

/**
 * Removes one attribution header from storage and keeps the rest intact.
 *
 * Used after successful registration to consume `x-affiliate-token` once while
 * preserving UTM/click attribution for the normal 30-day storage window.
 */
export function removeCustomerSourceHeaderFromStorage(headerName: UserSourceHeaderName): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const raw = window.localStorage.getItem(CUSTOMER_SOURCE_HEADERS_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw) as unknown;

    // Backward compatibility with previous format: plain header object.
    if (isObject(parsed) && !('headers' in parsed) && !('expiresAt' in parsed)) {
      const headers = sanitizeHeaders(parsed);
      delete headers[headerName];

      if (!Object.keys(headers).length) {
        window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
        return;
      }

      const payload: StoredCustomerSourceHeaders = {
        headers,
        expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
      };

      window.localStorage.setItem(CUSTOMER_SOURCE_HEADERS_KEY, JSON.stringify(payload));
      return;
    }

    if (!isObject(parsed) || !isObject(parsed.headers) || typeof parsed.expiresAt !== 'number') {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return;
    }

    if (Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return;
    }

    const headers = sanitizeHeaders(parsed.headers);
    delete headers[headerName];
    const expiredHeaders = sanitizeExpiredHeaders(parsed.expiredHeaders);

    if (!Object.keys(headers).length && !expiredHeaders.length) {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return;
    }

    const payload: StoredCustomerSourceHeaders = {
      headers,
      expiresAt: parsed.expiresAt,
      ...(expiredHeaders.length ? { expiredHeaders } : {}),
    };

    window.localStorage.setItem(CUSTOMER_SOURCE_HEADERS_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors; attribution cleanup should not block auth flows.
  }
}
